
import { supabase } from '@/integrations/supabase/client';
import { smartSearchResources } from '@/lib/smartSearch';

// Helper function to normalize content types to a single primary value
const normalizeContentType = (contentType: string): string => {
  if (!contentType) return '';
  // Take the first segment before comma and trim whitespace
  return contentType.split(',')[0].trim();
};

export interface Resource {
  "Resource Name": string;
  "Content Type": string;
  "Description": string | null;
  "Link": string | null;
  "Last Updated": string | null;
  "Topic Tags": string | null;
  "Recommended Use Case": string | null;
}

export interface ResourceFilters {
  contentTypes: string[];
  topics: string[];
  useCases: string[];
}

export const fetchResources = async (
  searchQuery?: string,
  filters?: ResourceFilters,
  sortBy: string = 'relevance'
): Promise<Resource[]> => {
  console.log('Fetching resources from ref_tools_resource view...');
  
  try {
    // Use a simple query without complex type inference
    const { data, error } = await supabase
      .from('Ref Tools Resource')
      .select('*')
      .returns<any[]>();

    if (error) {
      console.error('Error fetching resources:', error.code, error.message);
      throw error;
    }

    // Transform raw data to our Resource interface
    let resources: Resource[] = (data || []).map((item: any) => ({
      "Resource Name": String(item["Resource Name"] || ''),
      "Content Type": normalizeContentType(String(item["Content Type"] || '')),
      "Description": item["Description"] ? String(item["Description"]) : null,
      "Link": item["Link"] ? String(item["Link"]) : null,
      "Last Updated": item["Last Updated"] ? String(item["Last Updated"]) : null,
      "Topic Tags": item["Topic Tags"] ? String(item["Topic Tags"]) : null,
      "Recommended Use Case": item["Recommended Use Case"] ? String(item["Recommended Use Case"]) : null,
    }));

    // Apply smart search filter
    if (searchQuery && searchQuery.trim()) {
      resources = smartSearchResources(resources, searchQuery.trim(), 30);
    }

    // Apply content type filter
    if (filters?.contentTypes && filters.contentTypes.length > 0) {
      resources = resources.filter(resource => 
        filters.contentTypes.includes(resource["Content Type"])
      );
    }

    // Apply topic filter
    if (filters?.topics && filters.topics.length > 0) {
      resources = resources.filter(resource => {
        const topicTags = resource["Topic Tags"];
        if (!topicTags) return false;
        const topics = topicTags.split(',').map(t => t.trim());
        return filters.topics.some(filterTopic => 
          topics.some(topic => topic.toLowerCase().includes(filterTopic.toLowerCase()))
        );
      });
    }

    // Apply use case filter
    if (filters?.useCases && filters.useCases.length > 0) {
      resources = resources.filter(resource => {
        const useCases = resource["Recommended Use Case"];
        if (!useCases) return false;
        const cases = useCases.split(/[,•\n]/).map(c => c.trim()).filter(Boolean);
        return filters.useCases.some(filterUseCase => 
          cases.some(useCase => useCase.toLowerCase().includes(filterUseCase.toLowerCase()))
        );
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'title':
        resources.sort((a, b) => a["Resource Name"].localeCompare(b["Resource Name"]));
        break;
      case 'updated':
        resources.sort((a, b) => {
          const dateA = a["Last Updated"] ? new Date(a["Last Updated"]).getTime() : 0;
          const dateB = b["Last Updated"] ? new Date(b["Last Updated"]).getTime() : 0;
          return dateB - dateA;
        });
        break;
      case 'relevance':
      default:
        resources.sort((a, b) => a["Resource Name"].localeCompare(b["Resource Name"]));
        break;
    }

    console.log(`Fetched ${resources.length} resources from ref_tools_resource view`);
    return resources;
  } catch (error) {
    console.error('Error in fetchResources:', error);
    throw error;
  }
};

export const fetchFilterOptions = async () => {
  console.log('Fetching resource filter options...');
  
  try {
    const { data, error } = await supabase
      .from('Ref Tools Resource')
      .select('"Content Type", "Topic Tags", "Recommended Use Case"')
      .returns<any[]>();

    if (error) {
      console.error('Error fetching resource filters:', error.code, error.message);
      throw error;
    }

    const rawItems = data || [];

    // Extract unique content types
    const contentTypesSet = new Set<string>();
    const topicsSet = new Set<string>();
    const useCasesSet = new Set<string>();

    rawItems.forEach((item: any) => {
      // Content types - normalize to single primary content type
      if (item["Content Type"]) {
        const normalizedContentType = normalizeContentType(String(item["Content Type"]));
        if (normalizedContentType) {
          contentTypesSet.add(normalizedContentType);
        }
      }

      // Topics
      if (item["Topic Tags"]) {
        const topics = String(item["Topic Tags"]).split(',').map((t: string) => t.trim()).filter(Boolean);
        topics.forEach((topic: string) => topicsSet.add(topic));
      }

      // Use cases
      if (item["Recommended Use Case"]) {
        const cases = String(item["Recommended Use Case"]).split(/[,•\n]/).map((c: string) => c.trim()).filter(Boolean);
        cases.forEach((useCase: string) => useCasesSet.add(useCase));
      }
    });

    return {
      contentTypes: Array.from(contentTypesSet).sort(),
      topics: Array.from(topicsSet).sort(),
      useCases: Array.from(useCasesSet).sort()
    };
  } catch (error) {
    console.error('Error in fetchFilterOptions:', error);
    throw error;
  }
};

export const fetchSingleResource = async (resourceName: string): Promise<Resource | null> => {
  try {
    const response = await fetch(
      `https://oslzwjyyhugvrzrnwhuc.supabase.co/rest/v1/Ref%20Tools%20Resource?Resource%20Name=eq.${encodeURIComponent(resourceName)}&select=*`,
      {
        headers: {
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9zbHp3anl5aHVndnJ6cm53aHVjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY2NTgwMTQsImV4cCI6MjA3MjIzNDAxNH0.Aiph-i4szU1ZHIdR-H2w4dMQAqtTqyzSlY3-oza3vus',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9zbHp3anl5aHVndnJ6cm53aHVjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY2NTgwMTQsImV4cCI6MjA3MjIzNDAxNH0.Aiph-i4szU1ZHIdR-H2w4dMQAqtTqyzSlY3-oza3vus',
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();

    if (!data || data.length === 0) {
      return null;
    }

    const item = data[0];

    return {
      "Resource Name": String(item["Resource Name"] || ''),
      "Content Type": normalizeContentType(String(item["Content Type"] || '')),
      "Description": item["Description"] ? String(item["Description"]) : null,
      "Link": item["Link"] ? String(item["Link"]) : null,
      "Last Updated": item["Last Updated"] ? String(item["Last Updated"]) : null,
      "Topic Tags": item["Topic Tags"] ? String(item["Topic Tags"]) : null,
      "Recommended Use Case": item["Recommended Use Case"] ? String(item["Recommended Use Case"]) : null,
    };
  } catch (error) {
    console.error('Error in fetchSingleResource:', error);
    throw error;
  }
};
